<template>
  <div id="assignment_section" class="flex flex-col gap-6">
    <div id="assign_phone_number_to" class="flex flex-col gap-4">
      <span class="secondary_color text-[13px]">Assign Phone Number to</span>

      <a-radio-group
        v-model:value="internalFormData.assignedTo"
        @change="$emit('resetForm')"
      >
        <a-radio
          v-for="(assignee, index) in assignPhoneNumberTypesList"
          :key="`${assignee?.value}_radio_${index}`"
          :value="assignee?.value"
          >{{ assignee?.label }}</a-radio
        >
      </a-radio-group>
    </div>

    <div id="assign_to" class="flex flex-col gap-1">
      <span class="secondary_color text-[13px]">Assign to</span>

      <a-select
        v-if="internalFormData.assignedTo === assignCallTypes.USER"
        v-model:value="internalFormData.userId"
        class="w-full"
        placeholder="Select a user"
        show-search
        :filter-option="filterOptions"
      >
        <template #suffixIcon
          ><ThemeImage
            class="size-4 opacity-40"
            :icon="imageNames.BOTTOM_ARROW"
            alt="bottom_white_arrow"
        /></template>

        <a-select-option
          v-for="(user, index) in usersList"
          :key="`${user.id}_user_${index}`"
          :value="user.id"
        >
          <div class="flex flex-row items-center gap-2">
            {{ renderEntity(user) }}
          </div>
        </a-select-option>
      </a-select>

      <a-select
        v-if="internalFormData.assignedTo === assignCallTypes.TEAM"
        v-model:value="internalFormData.teamId"
        class="w-full"
        placeholder="Select a team"
        show-search
        :filter-option="filterOptions"
      >
        <template #suffixIcon
          ><ThemeImage
            class="size-4 opacity-40"
            :icon="imageNames.BOTTOM_ARROW"
            alt="bottom_white_arrow"
        /></template>

        <a-select-option
          v-for="(team, index) in teamsList"
          :key="`${team.id}_team_${index}`"
          :value="team.id"
        >
          <div class="flex flex-row items-center gap-2">
            {{ renderEntity(team) }}
          </div>
        </a-select-option>
      </a-select>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { renderEntity } from "@/src/utils/utils.js";
import { assignCallTypes } from "@/src/constants/callConstants.js";
import { assignPhoneNumberTypesList } from "@/src/constants/phoneNumberConstants.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import DashboardService from "@/src/api/DashboardService.js";
import ThemeImage from "@/src/components/ThemeImage.vue";

const props = defineProps({
  internalFormData: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["resetForm"]);

const usersList = ref([]);
const teamsList = ref([]);

const toFetchUsers = async () => {
  try {
    const response = await DashboardService.fetchUsers();
    if (response?.success) {
      usersList.value = response?.users;
      if (
        props.internalFormData.assignedTo === assignCallTypes.USER &&
        !props.internalFormData.userId
      ) {
        props.internalFormData.userId = usersList.value[0]?.id || null;
      }
    }
  } catch (error) {
    console.error("An error occurred while fetching users: ", error);
  }
};

const toFetchTeams = async () => {
  try {
    const response = await DashboardService.fetchTeams();
    if (response?.success) {
      teamsList.value = response?.teams;
      if (
        props.internalFormData.assignedTo === assignCallTypes.TEAM &&
        !props.internalFormData.teamId
      ) {
        props.internalFormData.teamId = teamsList.value[0]?.id || null;
      }
    }
  } catch (error) {
    console.error("An error occurred while fetching teams: ", error);
  }
};

const filterOptions = (input, option) => {
  try {
    if (!input?.trim()) return true;

    if (props.internalFormData.assignedTo === assignCallTypes.USER) {
      const name =
        usersList.value
          ?.find((user) => user?.id === option?.value)
          ?.name?.trim() || "";
      return name?.toLowerCase()?.includes(input?.trim()?.toLowerCase());
    }

    if (props.internalFormData.assignedTo === assignCallTypes.TEAM) {
      const name =
        teamsList.value
          ?.find((team) => team?.id === option?.value)
          ?.name?.trim() || "";
      return name?.toLowerCase()?.includes(input?.trim()?.toLowerCase());
    }

    return false;
  } catch (error) {
    console.error("An error occurred while filtering options: ", error);
    return false;
  }
};

onMounted(async () => {
  await Promise.all([toFetchUsers(), toFetchTeams()]);
});
</script>
